import React, { useState } from 'react';
import { useWatchableValue } from '@cotera/client/v0/actions/framework';
import { Input } from '@cotera/client/v0/components/ui/input';
import { Button } from '@cotera/client/v0/components/ui/button';
import { Text } from '@cotera/client/app/components/ui';
import { toast } from '@cotera/client/app/components/ui';
import { useQueryClient } from '@tanstack/react-query';
import { ResourceType } from '@cotera/api';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';
import { queryKeys } from '@cotera/client/app/hooks/query-cache-keys';
import {
  WebhookProviderConfigSection,
  findWebhookProviderCatalogEntry,
  useWebhookProviderCatalog,
} from '@cotera/client/app/v1/webhook-providers';
import type { WorkflowWebhookTriggerViewData } from '../types';
import { useWorkflowGraphMutations } from '../../../use-workflow-graph-mutations';
import { useWorkflowModel } from '../../../workflow.model';
import { isDraftOnlyTriggerInWorkflow } from '../../../workflow-draft-graph';
import { WorkflowIdempotencyKeyEditor } from '../../workflow-idempotency-key-editor';
import { useWorkflowWebhookTriggerEditModel } from '../../../hooks/use-workflow-webhook-trigger-edit-model';

export const WebhookTriggerEditor: React.FC<{
  workflowId: string;
  bindingId: string;
  trigger: WorkflowWebhookTriggerViewData;
  onSaved?: () => void;
}> = ({ workflowId, bindingId, trigger, onSaved }) => {
  const model = useWorkflowModel();
  const mutations = useWorkflowGraphMutations();
  const isDraftOnly = isDraftOnlyTriggerInWorkflow(
    model.getDraftState(),
    trigger.id,
    model.isNew
  );
  const editModel = useWorkflowWebhookTriggerEditModel({
    triggerId: trigger.id,
    isDraftOnly,
  });
  const draft = useWatchableValue(editModel.draftWatchable);
  const isReady = useWatchableValue(editModel.isReadyWatchable);
  const loadError = useWatchableValue(editModel.loadErrorWatchable);
  const settings = useWatchableValue(editModel.settingsWatchable);
  const client = useTenantedClient();
  const queryClient = useQueryClient();
  const orgId = useWhoami((s) => s.org.id);
  const { providers, isLoading: catalogLoading } = useWebhookProviderCatalog();
  const [saving, setSaving] = useState(false);

  const catalogEntry = findWebhookProviderCatalogEntry(
    providers,
    draft.providerKey ?? trigger.providerKey ?? 'generic'
  );

  const handleSave = async (): Promise<void> => {
    if (catalogEntry === undefined) {
      return;
    }
    setSaving(true);
    if (isDraftOnly) {
      const saved = await mutations.updateWebhookTrigger({
        bindingId,
        name: draft.name,
        webhookRowEnabled: draft.enabled,
        providerKey: draft.providerKey,
        verificationConfig: draft.verificationConfig,
        eventKey: draft.eventKey ?? undefined,
        eventConfig: draft.eventConfig,
        endpointSecret: draft.endpointSecret.trim() || undefined,
      });
      setSaving(false);
      if (!saved) {
        return;
      }
      editModel.clearOverrides();
      toast.success('Webhook trigger updated');
      onSaved?.();
      return;
    }
    const result = await client.resource.workflow.updateTrigger({
      workflowId,
      bindingId,
      triggerId: trigger.id,
      name: draft.name,
      webhookRowEnabled: draft.enabled,
      providerKey: draft.providerKey,
      verificationConfig: draft.verificationConfig,
      eventKey: draft.eventKey ?? undefined,
      eventConfig: draft.eventConfig,
      endpointSecret: draft.endpointSecret.trim() || undefined,
    });
    if (result.isErr()) {
      setSaving(false);
      toast.error(
        result.error.t === 'application'
          ? result.error.err.message
          : 'Failed to update webhook trigger'
      );
      return;
    }
    const idempotencyResult = await model.persistIdempotencyKey(
      model.getIdempotencyKey()
    );
    setSaving(false);
    if (!idempotencyResult.success) {
      toast.error(idempotencyResult.message);
      return;
    }
    editModel.clearOverrides();
    await editModel.refreshSettings();
    void queryClient.invalidateQueries({
      queryKey: queryKeys.resource.get({
        orgId,
        type: ResourceType.Workflow,
        id: workflowId,
      }),
    });
    // A saved secret flips the catalog's "already on file" flag for providers
    // that share one endpoint across the org.
    void queryClient.invalidateQueries({
      queryKey: queryKeys.webhookProviders.catalog({ orgId }),
    });
    toast.success('Webhook trigger updated');
    onSaved?.();
  };

  // Checked before the loading state: a settings fetch that failed is done
  // loading, and rendering the spinner for it is what left this panel saying
  // "Loading webhook settings…" indefinitely.
  if (loadError !== null) {
    return (
      <div className="space-y-2">
        <Text.Caption className="text-destructive block">
          {loadError}
        </Text.Caption>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => void editModel.refreshSettings()}
        >
          Retry
        </Button>
      </div>
    );
  }

  if (catalogLoading || catalogEntry === undefined || !isReady) {
    return (
      <Text.Caption className="text-muted-foreground block">
        Loading webhook settings…
      </Text.Caption>
    );
  }

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Text.Caption className="text-muted-foreground block">
          Name
        </Text.Caption>
        <Input
          value={draft.name}
          onChange={(event) =>
            editModel.patchDraft({ name: event.target.value })
          }
        />
      </div>
      <WebhookProviderConfigSection
        mode="edit"
        catalog={catalogEntry}
        draft={draft}
        onDraftChange={(patch) => editModel.patchDraft(patch)}
        triggerId={trigger.id}
        isDraft={isDraftOnly}
        deliveryUrl={isDraftOnly ? undefined : settings?.deliveryUrl}
        installStatus={isDraftOnly ? 'draft' : trigger.installStatus}
        secret={
          isDraftOnly ? 'save-workflow-to-reveal-secret' : settings?.secret
        }
        onSave={handleSave}
        saving={saving}
      />
      <WorkflowIdempotencyKeyEditor
        value={model.getIdempotencyKey()}
        onChange={(next) => model.setIdempotencyKey(next)}
        disabled={saving}
      />
      <Button
        type="button"
        className="w-full"
        disabled={saving}
        onClick={() => void handleSave()}
      >
        Save webhook settings
      </Button>
    </div>
  );
};
